import React from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useCreateCurrentAssessmentMutation } from "../../infrastructure/api/patientsApi";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful cancellation so the parent can refetch. */
  onSuccess?: () => void;
  /** Pre-formatted date/time string shown in the confirmation message. */
  dateTime?: string;
  patientId: string;
  currentStatus: string;
}

const CANCELLABLE_STATUSES: Record<
  string,
  "CC_CANCELLED" | "SCR_CANCELLED" | "RR_CANCELLED"
> = {
  CC_SCHEDULED: "CC_CANCELLED",
  CC_RESCHEDULED: "CC_CANCELLED",
  CC_IN_SESSION: "CC_CANCELLED",
  SCR_SCHEDULED: "SCR_CANCELLED",
  SCR_RESCHEDULED: "SCR_CANCELLED",
  RR_SCHEDULED: "RR_CANCELLED",
  RR_RESCHEDULED: "RR_CANCELLED",
  RR_IN_SESSION: "RR_CANCELLED",
};

const getAppointmentTypeLabel = (status: string, t: TFunction<"patients">): string => {
  const labels: Record<string, string> = {
    CC_SCHEDULED: t("cancelAppointmentDialog.typeClinicalContext"),
    CC_RESCHEDULED: t("cancelAppointmentDialog.typeClinicalContext"),
    CC_IN_SESSION: t("cancelAppointmentDialog.typeClinicalContext"),
    SCR_SCHEDULED: t("cancelAppointmentDialog.typeScreening"),
    SCR_RESCHEDULED: t("cancelAppointmentDialog.typeScreening"),
    RR_SCHEDULED: t("cancelAppointmentDialog.typeReportReview"),
    RR_RESCHEDULED: t("cancelAppointmentDialog.typeReportReview"),
    RR_IN_PROGRESS: t("cancelAppointmentDialog.typeReportReview"),
  };
  return labels[status] ?? t("cancelAppointmentDialog.typeDefault");
};

export const CancelScreeningApporintmentDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  onSuccess,
  dateTime,
  patientId,
  currentStatus,
}) => {
  const { t } = useTranslation("patients");
  const [cancelAssessment, { isLoading }] =
    useCreateCurrentAssessmentMutation();

  const cancelStatus = CANCELLABLE_STATUSES[currentStatus];
  const appointmentLabel = getAppointmentTypeLabel(currentStatus, t);

  const handleConfirm = async () => {
    if (!cancelStatus) {
      toast.error(t("cancelAppointmentDialog.invalidStatus", { status: currentStatus }));
      return;
    }

    try {
      await cancelAssessment({
        patientId,
        data: { status: cancelStatus },
      }).unwrap();
      toast.success(t("cancelAppointmentDialog.success"));
      onOpenChange(false);
      onSuccess?.();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast.error(
        error?.data?.message ??
          error?.data?.detail ??
          error?.message ??
          t("cancelAppointmentDialog.error"),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {t("cancelAppointmentDialog.title", { type: appointmentLabel })}
          </DialogTitle>
          <p className="text-sm text-gray-500 mt-1">
            {dateTime
              ? t("cancelAppointmentDialog.confirmWithDate", {
                  type: appointmentLabel,
                  date: dateTime,
                })
              : t("cancelAppointmentDialog.confirmNoDate", { type: appointmentLabel })}
          </p>
        </DialogHeader>

        <div className="flex justify-end gap-3 pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
            className="border-primary px-4 text-primary hover:bg-white hover:text-primary cursor-pointer"
          >
            {t("cancelAppointmentDialog.goBack")}
          </Button>

          <Button
            onClick={handleConfirm}
            loading={isLoading}
            className="bg-[#E53935] px-4 text-white hover:bg-[#E53935] cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("cancelAppointmentDialog.cancel")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
